// SPDX-License-Identifier: AGPL-3.0-or-later
import { useId, useState } from "react";
import { isoDate, yearOf } from "@almanac/domain";
import { monthName } from "@almanac/i18n";
import { CategoryDonut } from "../components/CategoryDonut.js";
import { CategoryOptions } from "../components/CategoryOptions.js";
import { YearCalendar } from "../components/YearCalendar.js";
import { useApp } from "../lib/app.js";
import { amountOf } from "../lib/budgetTree.js";
import { monthlySpending, spendingBudget } from "../lib/figures.js";
import { useI18n } from "../lib/i18n.js";
import { useStore } from "../lib/store.js";

/** Annual spending: the year's donut, a month-by-month table, and the full year of calendars. */
export function Year() {
  const { store, today } = useApp();
  const state = useStore(store);
  const { t, money, locale, category } = useI18n();
  const [year, setYear] = useState(yearOf(today));
  const [scope, setScope] = useState<string>("");
  const scopeId = useId();
  const id = scope === "" ? null : scope;
  const budget = id === null ? spendingBudget(state.plan) : amountOf(state.plan, id);
  const spent = monthlySpending(state.transactions, year, today, id);
  const elapsed = spent.filter((s) => s !== null).length;
  const spentTotal = spent.reduce<number>((s, x) => s + (x ?? 0), 0);
  const leftFor = (planned: number, actual: number) => (
    <span className={actual > planned ? "negative" : "positive"}>
      {actual > planned ? t("year.over", { amount: money(actual - planned) }) : money(planned - actual)}
    </span>
  );

  return (
    <>
      <div className="card period-bar" role="group" aria-label={t("year.choose")}>
        <div className="month-stepper">
          <button type="button" onClick={() => setYear(year - 1)} aria-label={t("year.previous")}>
            ‹
          </button>
          <span className="period-label" aria-live="polite">
            {year}
          </span>
          <button type="button" onClick={() => setYear(year + 1)} aria-label={t("year.next")}>
            ›
          </button>
          {year !== yearOf(today) && (
            <button type="button" onClick={() => setYear(yearOf(today))}>
              {t("year.thisYear")}
            </button>
          )}
        </div>
      </div>

      <div className="grid dash-mid" style={{ marginTop: 0 }}>
        <section className="card" aria-labelledby="year-donut-title">
          <h2 id="year-donut-title">{t("year.donut", { year })}</h2>
          <CategoryDonut
            key={year}
            title={t("year.byCategory")}
            transactions={state.transactions}
            start={isoDate(year, 1, 1)}
            end={isoDate(year, 12, 31)}
            empty={t("dash.emptyYear", { year })}
          />
        </section>
        <section className="card" aria-labelledby="year-table-title">
          <h2 id="year-table-title">{t("year.table")}</h2>
          <div className="field">
            <label htmlFor={scopeId}>{t("year.category")}</label>
            <select id={scopeId} value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="">{t("year.all")}</option>
              <CategoryOptions withIncome={false} />
            </select>
          </div>
          <p className="hint">{t("year.tableHint")}</p>
          <div className="table-wrap">
          <table className="data year-table">
            <caption className="visually-hidden">
              {t("year.caption", { name: id === null ? t("year.all") : category(id), year })}
            </caption>
            <thead>
              <tr>
                <th scope="col">{t("year.month")}</th>
                <th scope="col" className="num">
                  {t("dash.budget")}
                </th>
                <th scope="col" className="num">
                  {t("dash.spent")}
                </th>
                <th scope="col" className="num">
                  {t("dash.left")}
                </th>
              </tr>
            </thead>
            <tbody>
              {spent.map((s, i) => (
                <tr key={i}>
                  <th scope="row">{monthName(i + 1, locale)}</th>
                  <td className="num">{money(budget)}</td>
                  <td className="num">{s === null ? <span className="hint">{t("year.notYet")}</span> : money(s)}</td>
                  <td className="num">{s === null ? "" : leftFor(budget, s)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row">{t("year.total", { count: elapsed })}</th>
                <td className="num">{money(budget * elapsed)}</td>
                <td className="num">{money(spentTotal)}</td>
                <td className="num">{elapsed > 0 ? leftFor(budget * elapsed, spentTotal) : ""}</td>
              </tr>
            </tfoot>
          </table>
          </div>
        </section>
      </div>

      <YearCalendar key={year} transactions={state.transactions} plan={state.plan} today={today} year={year} />
    </>
  );
}
